import { useEffect, useState } from "react";
import { Modal, Form, Button, Spinner, Row, Col, Badge } from "react-bootstrap";
import { getDriversList, getMyVehicles, upcomingBookingsAPI, updateBooking } from "../../services/Networkcall";
import { errorAlert, successAlert } from "../common/Alert";
import { RiMapPinLine, RiCarLine, RiUserLine, RiHashtag, RiCloseCircleLine } from "react-icons/ri";
import Select from "react-select";

export const UpdateBookingModal = ({ show, closeModal, booking, setRefetch }) => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [driversList, setDriversList] = useState([]);
    const [vehiclesList, setVehiclesList] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);

    const hasDriver = !!booking?.driver;
    const hasVehicle = !!booking?.vehicle_info?.vehicleName;

    
    useEffect(() => {
        setSelectedDriver(null);
        setSelectedVehicle(null);
    }, [booking]);

    const fetchDriversList = async () => {
        try {
            const res = await getDriversList();
            if (res?.success) {
                const options = res?.data?.map((d) => ({
                    value: d?._id,
                    label: `${d?.userName} ${d?.phoneNumber ? `(${d?.phoneNumber})` : ""}`,
                }));
                setDriversList(options || []);
            } else {
                errorAlert({ message: res?.message });
            }
        } catch (err) {
            errorAlert({ message: err?.response?.data?.message || err?.message || "Something went wrong." });
        }
    };

    const fetchVehiclesList = async () => {
        try {
            const res = await getMyVehicles();
            if (res?.success) {
                const options = res?.data?.map((v) => ({
                    value: v?._id,
                    label: `${v?.vehicleName} ${v?.licensePlate ? `(${v?.licensePlate})` : ""}`,
                }));
                setVehiclesList(options || []);
            } else {
                errorAlert({ message: res?.message });
            }
        } catch (err) {
            errorAlert({ message: err?.response?.data?.message || err?.message || "Something went wrong." });
        }
    };

    useEffect(() => {
        fetchDriversList();
        fetchVehiclesList();
    }, []);


    const handleSubmit = async () => {
        if (!selectedDriver && !hasDriver) return errorAlert({ message: "Please select a driver." });
        if (!selectedVehicle && !hasVehicle) return errorAlert({ message: "Please select a vehicle." });
        setSubmitting(true);
        try {
            const payload = {
                "bookingId": booking?._id,
                "assignedUserId": selectedDriver?.value,
                "vehicleId": selectedVehicle?.value
            }
            const res = await updateBooking(payload);
            if (res?.success) {
                successAlert({ message: res?.message || "Booking updated successfully." });
                setRefetch(true);
                closeModal();
            } else {
                errorAlert({ message: res?.message || "Failed to update booking." });
            }
        } catch (err) {
            errorAlert({ message: err?.message || "Failed to update booking." });
        } finally {
            setSubmitting(false);
        }
    };



    return (
        <Modal show={show} onHide={closeModal} centered size="md"  >
            <RiCloseCircleLine size={25} color="#818181" onClick={closeModal} className="position-absolute end-0 top-0 m-1 cursor-pointer z-1" />
            <Modal.Header className="border-0 pb-0" />

            <Modal.Body className="px-4 pt-2 pb-4">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2 text-muted small">Loading...</p>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-3">

                        <div className="border rounded-3 overflow-hidden">
                            <div className="bg-primary bg-opacity-10 px-3 py-2 border-bottom">
                                <span className="fw-semibold text-primary small">Booking Details</span>
                            </div>
                            <div className="px-3 py-2">
                                <Row className="gy-2">
                                    <Col xs={6}>
                                        <div className="d-flex align-items-center gap-2">
                                            <RiHashtag className="text-muted" size={14} />
                                            <div>
                                                <p className="mb-0 text-muted" >Booking ID</p>
                                                <p className="mb-0 fw-semibold small">#{booking?.bookingId}</p>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="d-flex align-items-center gap-2">
                                            <div>
                                                <p className="mb-0 text-muted">Status</p>
                                                <Badge bg="success-subtle m-0" text="success" className="fw-semibold">
                                                    {booking?.booking_status}
                                                </Badge>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={12}>
                                        <div className="d-flex align-items-start gap-2">
                                            <RiMapPinLine className="text-muted mt-1" size={14} />
                                            <div>
                                                <p className="mb-0 text-muted">Location</p>
                                                <p className="mb-0 fw-semibold small">{booking?.location_info?.locationName}</p>
                                                <p className="mb-0 text-muted">{booking?.location_info?.locationAddress}</p>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </div>

                        {/* Driver Section */}
                        <div className="border rounded-3 overflow-hidden">
                            <div className="bg-light px-3 py-2 border-bottom d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-2">
                                    <RiUserLine className="text-primary" size={15} />
                                    <span className="fw-semibold small">Driver</span>
                                </div>
                            </div>
                            <div className="px-3 py-2">
                                {hasDriver ? (
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{ width: 40, height: 40 }}>
                                            <RiUserLine className="text-primary" size={18} />
                                        </div>
                                        <div>
                                            <p className="mb-0 fw-semibold small">{booking?.driver?.userName}</p>
                                                <small className="mb-0 text-muted" >{booking?.driver?.email}</small>
                                            <p className="mb-0 text-muted small" >{booking?.driver?.phoneNumber}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <Select
                                        value={selectedDriver}
                                        onChange={(option) => setSelectedDriver(option)}
                                        options={driversList}
                                        placeholder={hasDriver ? "Change Driver" : "Select Driver"}
                                        isSearchable
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Vehicle Section */}
                        <div className="border rounded-3 overflow-hidden">
                            <div className="bg-light px-3 py-2 border-bottom d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-2">
                                    <RiCarLine className="text-primary" size={15} />
                                    <span className="fw-semibold small">Vehicle</span>
                                </div>
                            </div>
                            <div className="px-3 py-2">
                                {hasVehicle ? (
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{ width: 40, height: 40 }}>
                                            <RiCarLine className="text-primary" size={18} />
                                        </div>
                                        <div>
                                            <p className="mb-0 fw-semibold small">{booking?.vehicle_info?.vehicleName}</p>
                                            <p className="mb-0 text-muted" >{booking?.vehicle_info?.licensePlate}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <Select
                                        value={selectedVehicle}
                                        onChange={(option) => setSelectedVehicle(option)}
                                        options={vehiclesList}
                                        placeholder={hasVehicle ? "Change Vehicle" : "Select Vehicle"}
                                        isSearchable
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                )}
                            </div>
                        </div>

                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0 px-4">
                <Button variant="outline-secondary" onClick={closeModal} disabled={submitting} className="px-4">
                    Cancel
                </Button>
                {(!hasDriver || !hasVehicle) &&
                    <Button variant="primary" onClick={handleSubmit} disabled={submitting || loading} className="px-4">
                        {submitting ? <><Spinner size="sm" animation="border" className="me-2" />Updating...</> : "Update Booking"}
                    </Button>}
            </Modal.Footer>
        </Modal>
    );
};